import { useMemo, useState } from "react";
import { useLocation } from "react-router-dom";
import HoursMonthCell from "../../Components/HoursMonthCell";

const MONTHS = [
  { key: "gen", label: "Gen" },
  { key: "feb", label: "Feb" },
  { key: "mar", label: "Mar" },
  { key: "apr", label: "Apr" },
  { key: "mag", label: "Mag" },
  { key: "giu", label: "Giu" },
  { key: "lug", label: "Lug" },
  { key: "ago", label: "Ago" },
  { key: "set", label: "Set" },
  { key: "ott", label: "Ott" },
  { key: "nov", label: "Nov" },
  { key: "dic", label: "Dic" },
];

const emptyMonths = () =>
  MONTHS.reduce((acc, month) => {
    acc[month.key] = { hours: null, status: "available" };
    return acc;
  }, {});

const MOCK_BANDOS = [
  { id: "bando-sad-2026", name: "SAD 2026", year: 2026 },
  { id: "bando-edu-2026", name: "Educativa domiciliare 2026", year: 2026 },
];

const MOCK_ROWS = [
  {
    id: "1",
    announcementId: "bando-sad-2026",
    bando: "SAD 2026",
    year: 2026,
    fiscalCode: "RSSMRA80A01H501U",
    lastName: "Rossi",
    firstName: "Mario",
    months: {
      ...emptyMonths(),
      gen: { hours: 20, status: "paid" },
      feb: { hours: 30, status: "paid" },
      mar: { hours: 18, status: "available" },
      apr: { hours: 12, status: "available" },
    },
  },
  {
    id: "2",
    announcementId: "bando-sad-2026",
    bando: "SAD 2026",
    year: 2026,
    fiscalCode: "BNCNNA75C41A662X",
    lastName: "Bianchi",
    firstName: "Anna",
    months: {
      ...emptyMonths(),
      gen: { hours: 16, status: "billed" },
      feb: { hours: 16, status: "billed" },
      mar: { hours: 8, status: "available" },
    },
  },
  {
    id: "3",
    announcementId: "bando-sad-2026",
    bando: "SAD 2026",
    year: 2026,
    fiscalCode: "VRDGPP70D15L736K",
    lastName: "Verdi",
    firstName: "Giuseppe",
    months: {
      ...emptyMonths(),
      gen: { hours: 10, status: "available" },
      feb: { hours: 10, status: "available" },
    },
  },
  {
    id: "4",
    announcementId: "bando-edu-2026",
    bando: "Educativa domiciliare 2026",
    year: 2026,
    fiscalCode: "NREGNN85E55H501Y",
    lastName: "Neri",
    firstName: "Giovanna",
    months: {
      ...emptyMonths(),
      gen: { hours: 24, status: "available" },
      feb: { hours: 20, status: "available" },
      mar: { hours: 22, status: "available" },
    },
  },
];

function useQuery() {
  return new URLSearchParams(useLocation().search);
}

function formatHours(value) {
  if (value === null || value === undefined || value === "") {
    return "—";
  }
  return Number(value).toLocaleString("it-IT", {
    minimumFractionDigits: 0,
    maximumFractionDigits: 2,
  });
}

function rowTotal(months) {
  return MONTHS.reduce((sum, month) => {
    const hours = months[month.key]?.hours;
    return sum + (Number(hours) || 0);
  }, 0);
}

function HoursGrid() {
  const query = useQuery();
  const queryBandoId = query.get("id");
  const initialBando =
    MOCK_BANDOS.find((bando) => bando.id === queryBandoId)?.id ||
    MOCK_BANDOS[0].id;

  const [selectedBandoId, setSelectedBandoId] = useState(initialBando);
  const [search, setSearch] = useState("");
  const [rows, setRows] = useState(MOCK_ROWS);

  const selectedBando = MOCK_BANDOS.find((bando) => bando.id === selectedBandoId);

  const visibleRows = useMemo(() => {
    const term = search.trim().toLowerCase();
    return rows.filter((row) => {
      if (row.announcementId !== selectedBandoId) {
        return false;
      }
      if (!term) {
        return true;
      }
      return (
        row.fiscalCode.toLowerCase().includes(term) ||
        row.lastName.toLowerCase().includes(term) ||
        row.firstName.toLowerCase().includes(term)
      );
    });
  }, [rows, selectedBandoId, search]);

  const updateMonth = (rowId, monthKey, nextCell) => {
    setRows((current) =>
      current.map((row) =>
        row.id === rowId
          ? { ...row, months: { ...row.months, [monthKey]: nextCell } }
          : row
      )
    );
  };

  return (
    <div className="hours-grid-page">
      <h1>Ore fatturate</h1>
      <div className="clearfix col-lg-10 py-lg-2 px-0">
        <p>
          Registro ore per utente e mese. Clicca una cella mese per inserire o
          modificare le ore. Anagrafica, bando e mesi già in bollettino o pagati
          non sono modificabili.
        </p>
      </div>

      <div className="row mt-3 mb-3 align-items-end">
        <div className="col-lg-4 mb-3">
          <label htmlFor="hours-bando">Bando</label>
          <select
            id="hours-bando"
            className="form-control"
            value={selectedBandoId}
            onChange={(event) => setSelectedBandoId(event.target.value)}
          >
            {MOCK_BANDOS.map((bando) => (
              <option key={bando.id} value={bando.id}>
                {bando.name}
              </option>
            ))}
          </select>
        </div>
        <div className="col-lg-2 mb-3">
          <label htmlFor="hours-year">Anno</label>
          <input
            id="hours-year"
            className="form-control"
            value={selectedBando?.year || ""}
            readOnly
          />
        </div>
        <div className="col-lg-4 mb-3">
          <label htmlFor="hours-search">Cerca utente</label>
          <input
            id="hours-search"
            className="form-control"
            type="search"
            placeholder="CF, cognome o nome"
            value={search}
            onChange={(event) => setSearch(event.target.value)}
          />
        </div>
      </div>

      <div className="hours-grid-legend mb-3">
        <span className="hours-legend-item is-available">Modificabile</span>
        <span className="hours-legend-item is-billed">In bollettino</span>
        <span className="hours-legend-item is-paid">Pagato</span>
      </div>

      <div className="table-responsive rounded hours-grid-wrap">
        <table
          className="table table-hover table-sm hours-grid-table"
          style={{ border: "1px solid #d6dce3" }}
        >
          <thead>
            <tr>
              <th className="hours-sticky-col hours-sticky-cf">Codice fiscale</th>
              <th className="hours-sticky-col hours-sticky-name">Nominativo</th>
              {MONTHS.map((month) => (
                <th key={month.key} className="text-center hours-month-head">
                  {month.label}
                </th>
              ))}
              <th className="text-center">Totale</th>
            </tr>
          </thead>
          <tbody>
            {visibleRows.length === 0 && (
              <tr>
                <td colSpan={MONTHS.length + 3} className="text-center py-4">
                  <b>Nessun utente per i filtri selezionati.</b>
                </td>
              </tr>
            )}
            {visibleRows.map((row) => (
              <tr key={row.id} height="3em">
                <td className="text-nowrap hours-sticky-col hours-sticky-cf">
                  {row.fiscalCode}
                </td>
                <td className="text-nowrap hours-sticky-col hours-sticky-name">
                  {row.lastName} {row.firstName}
                </td>
                {MONTHS.map((month) => (
                  <HoursMonthCell
                    key={month.key}
                    monthKey={month.key}
                    cell={row.months[month.key] || { hours: null, status: "available" }}
                    onChange={(monthKey, nextCell) =>
                      updateMonth(row.id, monthKey, nextCell)
                    }
                  />
                ))}
                <td className="text-center font-weight-bold">
                  {formatHours(rowTotal(row.months))}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      <p className="text-muted small mt-2 mb-0">
        Dati di esempio: nessuna API collegata. I mesi pagati o in bollettino
        restano bloccati fino all&apos;annullo del pagamento.
      </p>
    </div>
  );
}

export default HoursGrid;
